// my agent to top navigation bar
// b2c dropdown box -> my agent, sharing hub, discussion board(reddit)
// my agent ->
// sharing hub -> currnet home page (landing to later)
// discussion board(reddit) -> 

// src/components/MyAgents.js
import React, { useEffect, useState } from 'react';
import { auth, db, storage } from '../firebase';
import { collection, query, where, onSnapshot, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './MyAgents.css';

function MyAgents() {
  const [agents, setAgents] = useState([]);
  const [thumbnails, setThumbnails] = useState({}); // Map of agentId to thumbnail URL (local override)
  const [thumbnailLoading, setThumbnailLoading] = useState({}); // Map of agentId to loading status
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [currentChatAgent, setCurrentChatAgent] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");

  const currentUser = auth.currentUser;

  // Fetch agents for the current user from Firestore
  useEffect(() => {
    if (!currentUser) return;
    const q = query(collection(db, 'agents'), where('owner', '==', currentUser.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const agentsData = [];
      snapshot.forEach((docSnap) => {
        agentsData.push({ id: docSnap.id, ...docSnap.data() });
      });
      setAgents(agentsData);
    });
    return () => unsubscribe();
  }, [currentUser]);

  // Start Chat: Initialize chat modal with a system message using the agent's stored configuration data.
  const handleStartChat = (agent) => {
    const systemMessage = {
      role: "system",
      content: `This is a custom AI agent named ${agent.name}.`
    };
    // Description: ${agent.description}
    // Instructions: ${agent.instructions}
    // Capabilities: ${agent.capabilities.join(', ')}
    setCurrentChatAgent(agent);
    setChatMessages([systemMessage]);
    setChatModalOpen(true);
  };

  const handleCloseChatModal = () => {
    setChatModalOpen(false);
    setChatMessages([]);
    setChatInput("");
    setCurrentChatAgent(null);
  };

  // Handle sending a chat message and processing the response from OpenAI's Chat API
  const handleSendChatMessage = async () => {
    if (chatInput.trim() === "") return;
    const updatedMessages = [...chatMessages, { role: "user", content: chatInput }];
    setChatMessages(updatedMessages);
    setChatInput("");

    const modelToUse =
      currentChatAgent.model === "GPT-4o" ? "gpt-4o-2024-08-06" : currentChatAgent.model;
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    if (!apiKey) {
      alert("OpenAI API key is not set.");
      return;
    }
    try {
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: modelToUse,
          messages: updatedMessages,
          max_tokens: 150,
          temperature: 0.7
        })
      });
      const data = await response.json();
      if (data.error) {
        setChatMessages(prev => [
          ...prev,
          { role: "assistant", content: "Error: " + data.error.message }
        ]);
      } else {
        const assistantMessage = data.choices[0].message.content;
        setChatMessages(prev => [...prev, { role: "assistant", content: assistantMessage }]);
      }
    } catch (error) {
      console.error("Error calling OpenAI Chat API:", error);
      setChatMessages(prev => [
        ...prev,
        { role: "assistant", content: "Error: " + error.message }
      ]);
    }
  };

  // Create Thumbnail: Use the Firebase Cloud Function proxy to generate an image,
  // then upload it to Firebase Storage and update the Firestore agent document.
  const handleCreateThumbnail = async (agent) => {
    try {
      // Set loading indicator for this agent
      setThumbnailLoading(prev => ({ ...prev, [agent.id]: true }));
      
      // Use the deployed Cloud Function URL (update with your actual URL)
      const proxyUrl = "https://us-central1-ai-depot-c4820.cloudfunctions.net/generateThumbnail";
      // Update prompt: add "Do not include any text in the image."
      const prompt = `Thumbnail for custom AI: ${agent.name}. Description: ${agent.description}. Do not include any text in the image.`;
      const response = await fetch(proxyUrl, { 
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, aspect_ratio: "2:3" })
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`${response.status}: ${errorText}`);
      }
      const blob = await response.blob();
      // Upload the image to Firebase Storage under 'thumbnails/{agent.id}.png'
      const storageRef = ref(storage, `thumbnails/${agent.id}.png`);
      await uploadBytes(storageRef, blob);
      const downloadURL = await getDownloadURL(storageRef);
      // Update Firestore document with the thumbnail URL
      const agentRef = doc(db, 'agents', agent.id);
      await updateDoc(agentRef, { thumbnailUrl: downloadURL });
      setThumbnails(prev => ({ ...prev, [agent.id]: downloadURL }));
      // Clear the loading indicator
      setThumbnailLoading(prev => ({ ...prev, [agent.id]: false }));
    } catch (error) {
      console.error("Error generating thumbnail:", error);
      alert("Error generating thumbnail: " + error.message);
      setThumbnailLoading(prev => ({ ...prev, [agent.id]: false }));
    }
  };

  // Toggle share status for an agent in Firestore
  const toggleShare = async (agent) => {
    try {
      const agentRef = doc(db, 'agents', agent.id);
      await updateDoc(agentRef, { share: !agent.share });
    } catch (error) {
      console.error("Error updating share status:", error);
      alert("Error updating share status: " + error.message);
    }
  };

  return (
    <div className="myagents-container">
      <h2>My Agents</h2>
      {agents.length === 0 ? (
        <p>No custom AI models created yet.</p>
      ) : (
        <div className="agents-list">
          {agents.map((agent) => (
            <div key={agent.id} className="agent-card">
              <h3>{agent.name}</h3>
              <p>{agent.description}</p>
              <p>
                <strong>Model:</strong> {agent.model}
              </p>
              <div className="agent-buttons">
                <button onClick={() => handleStartChat(agent)}>Start Chat</button>
                <button onClick={() => handleCreateThumbnail(agent)}>Create Thumbnail</button>
                <button onClick={() => toggleShare(agent)}>
                  {agent.share ? 'Stop Sharing' : 'Share'}
                </button>
              </div>
              {thumbnailLoading[agent.id] ? (
                <div className="thumbnail-loading">
                  <p>Preparing your AI agent’s image...</p>
                  {/* Optionally, add a progress bar here if you have progress info */}
                </div>
              ) : (
                agent.thumbnailUrl && (
                  <div className="thumbnail-container">
                    <img src={agent.thumbnailUrl} alt="Thumbnail" />
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}

      {/* Chat Modal */}
      {chatModalOpen && (
        <div className="chat-modal-overlay">
          <div className="chat-modal">
            <div className="chat-modal-header">
              <h3>Chat with {currentChatAgent.name}</h3>
              <button onClick={handleCloseChatModal}>X</button>
            </div>
            <div className="chat-container">
              {chatMessages.map((msg, index) => (
                <div key={index} className={`chat-message ${msg.role}`}>
                  <div className="chat-message-content">{msg.content}</div>
                </div>
              ))}
            </div>
            <div className="chat-input-container">
              <input
                type="text"
                placeholder="Type your message..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
              />
              <button className="send-button" onClick={handleSendChatMessage}>Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyAgents;
